import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { SkeletonLoader, TagSelectV2, UI_BODY_SEMIBOLD_SM, UI_BODY_SM, UI_CAPTION_DF, UI_HEADLINE_SM, colors2 } from "oolib";

/**
 * The empty chat on a split-mode tenant: a greeting, the tenant's signal
 * categories as chips, and a few suggested questions built from the Topics
 * tab's tree (GET /api/aiChat/suggestions). Every card carries the anchor it
 * will send — a click asks the question pinned to that topic, so the answer
 * is built from the same observations the Topics card counts.
 *
 * Deliberately just questions: the card must not read like half an answer.
 * The numbers behind a card (counts, shares, trend) live in the answer.
 * Cards use the chat's own reference-card look (grey ground, no border, no
 * icon) so the landing reads as part of the chat, with the category as the
 * one quiet accent — the same dot the Topics tab uses for its category rows.
 */

const MAX_CARDS = 4;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1rem 0 1.5rem;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
  gap: 0.8rem;
`;

// Same chrome as the reference cards under an answer (Messages/AI Chunks):
// #F3F3F3 ground, 6px radius, 1rem padding, no border. Hover darkens a step.
const Card = styled.button`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border: none;
  border-radius: 6px;
  background: #f3f3f3;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: ${colors2.grey10};
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// The Topics tab's category mark (CardGridLayout CategoryDot).
const Dot = styled.span`
  width: 8px;
  height: 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Category = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SuggestionCard = ({ card, theme, onPick, disabled }) => (
  <Card type="button" disabled={disabled} onClick={() => !disabled && onPick(card)}>
    {card.category && (
      <Category>
        <Dot $color={theme.fillOf(card.category)} />
        <UI_CAPTION_DF style={{ color: colors2.grey60 }}>{theme.displayOf(card.category)}</UI_CAPTION_DF>
      </Category>
    )}
    <UI_BODY_SEMIBOLD_SM>{card.text}</UI_BODY_SEMIBOLD_SM>
  </Card>
);

/**
 * @param data      the suggestions endpoint payload ({ enabled, categories, suggestions })
 * @param theme     buildSignalCategoryTheme(...) — shared with the Topics tab
 * @param onPick    (card) => void — sends card.text with card.anchor
 */
/**
 * @param copy      resolveAskAICopy(prioritiesCopy, surface) — the opening
 *                  lines for this surface, tenant-overridable. The chat opens
 *                  an empty page and gets a greeting; the dashboard widget
 *                  sits in a card that already has a heading and gets none.
 * @param showCopy  false on a surface whose card already says what this is.
 */
export const SuggestionsLanding = ({ data, status, theme, firstName, copy = null, showCopy = true, onPick, disabled }) => {
  const [category, setCategory] = useState(null);

  const cards = useMemo(() => {
    const all = data?.suggestions || [];
    if (!category) return all.filter((s) => s.isDefault).slice(0, MAX_CARDS);
    // One category: its cards in topic order, the default set's members first
    // so the largest topics keep the top row.
    return all
      .filter((s) => s.category === category)
      .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
      .slice(0, MAX_CARDS);
  }, [data, category]);

  const title =
    firstName && copy?.titleWithName
      ? copy.titleWithName.replace("{name}", firstName)
      : copy?.title || (firstName ? `Hi ${firstName}, what would you like to know?` : "What would you like to know?");
  const subtitle = copy?.subtitle || "Pick a question about a topic your customers raise, or ask your own below.";

  return (
    <Wrap>
      {showCopy && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <UI_HEADLINE_SM>{title}</UI_HEADLINE_SM>
          <UI_BODY_SM style={{ color: colors2.grey60 }}>{subtitle}</UI_BODY_SM>
        </div>
      )}

      {status === "loading" ? (
        <Grid>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonLoader key={i} style={{ height: "5.5rem", borderRadius: "6px" }} />
          ))}
        </Grid>
      ) : (
        <>
          <Row>
            <TagSelectV2 display="All" value="__all" isSelected={!category} onClick={() => setCategory(null)} />
            {(data?.categories || [])
              .filter((c) => c.count > 0)
              .map((c) => {
                const opt = theme.options.find((o) => o.value === c.value);
                return (
                  <TagSelectV2
                    key={c.value}
                    display={c.display}
                    value={c.value}
                    color={opt?.color}
                    isSelected={category === c.value}
                    onClick={() => setCategory((prev) => (prev === c.value ? null : c.value))}
                  />
                );
              })}
          </Row>
          <Grid>
            {cards.map((card) => (
              <SuggestionCard key={card.id} card={card} theme={theme} onPick={onPick} disabled={disabled} />
            ))}
          </Grid>
          {cards.length === 0 && <UI_BODY_SM style={{ color: colors2.grey60 }}>No suggestions for this category yet.</UI_BODY_SM>}
        </>
      )}
    </Wrap>
  );
};
